// AuditLogsPanel — Panneau admin "Journal d'audit" : trace structurée de toutes
// les actions critiques de l'admin (approbations, blocages, suppressions, nettoyage
// doublons, bascule météo, etc.) avec acteur, cible, impact mesurable et horodatage.

import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  History, Search, RefreshCw, Shield, AlertTriangle,
  CheckCircle, Ban, Unlock, Trash2, Scissors, Cloud, X, Info, Loader2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ICONS = {
  check: CheckCircle,
  shield: Shield,
  ban: Ban,
  unlock: Unlock,
  trash: Trash2,
  scissors: Scissors,
  cloud: Cloud,
  x: X,
  info: Info,
};

const SEVERITY_STYLES = {
  info: "bg-blue-100 text-blue-800 border-blue-300",
  warn: "bg-amber-100 text-amber-800 border-amber-300",
  crit: "bg-rose-100 text-rose-800 border-rose-300",
};

const SEVERITY_LABEL = {
  info: "Info",
  warn: "Attention",
  crit: "Critique",
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function Impact({ impact }) {
  if (!impact || typeof impact !== "object" || Object.keys(impact).length === 0) {
    return <span className="text-slate-400 italic text-xs">—</span>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {Object.entries(impact).map(([k, v]) => (
        <span
          key={k}
          className="bg-slate-100 text-slate-800 px-2 py-0.5 rounded text-[11px] font-black uppercase tracking-wide"
        >
          {k.replace(/_/g, " ")}: <span className="text-indigo-700">{String(v)}</span>
        </span>
      ))}
    </div>
  );
}

export default function AuditLogsPanel() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ total: 0, critical_30d: 0 });
  const [actionTypes, setActionTypes] = useState({});
  const [loading, setLoading] = useState(false);
  const [filterAction, setFilterAction] = useState("");
  const [filterSeverity, setFilterSeverity] = useState("");
  const [filterActor, setFilterActor] = useState("");
  const [testingWhatsapp, setTestingWhatsapp] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 150 };
      if (filterAction) params.action = filterAction;
      if (filterSeverity) params.severity = filterSeverity;
      if (filterActor.trim()) params.actor = filterActor.trim();
      const res = await axios.get(`${API}/admin/audit-logs`, { params });
      setLogs(res.data?.logs || []);
      setStats(res.data?.stats || { total: 0, critical_30d: 0 });
      setActionTypes(res.data?.action_types || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [filterAction, filterSeverity, filterActor]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleTestWhatsapp = async () => {
    setTestingWhatsapp(true);
    try {
      const res = await axios.post(`${API}/admin/notifications/test-whatsapp`, {});
      const d = res.data || {};
      if (d.ok) {
        toast.success(`✅ WhatsApp envoyé (sid=${d.message_sid})`);
      } else if (!d.configured) {
        toast.error("⚠️ Twilio non configuré — renseignez les clés dans backend/.env");
      } else {
        toast.error(d.detail || "Échec d'envoi");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de test");
    } finally {
      setTestingWhatsapp(false);
    }
  };

  return (
    <Card className="bg-white shadow-lg" data-testid="audit-logs-panel">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle className="flex items-center gap-2 text-slate-800">
            <History className="w-5 h-5" />
            Journal d'audit admin
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 bg-slate-100 px-2 py-1 rounded">
              {stats.total} entrées
            </span>
            {stats.critical_30d > 0 && (
              <span className="text-[10px] font-black uppercase tracking-widest text-rose-700 bg-rose-100 px-2 py-1 rounded flex items-center gap-1">
                <AlertTriangle size={10} /> {stats.critical_30d} critiques aujourd'hui
              </span>
            )}
          </CardTitle>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleTestWhatsapp}
              disabled={testingWhatsapp}
              data-testid="test-whatsapp-btn"
            >
              {testingWhatsapp ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Shield className="w-3.5 h-3.5" />}
              Test WhatsApp
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchLogs}
              disabled={loading}
              data-testid="refresh-audit-btn"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Rafraîchir
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Filtres */}
        <div className="flex flex-wrap gap-3 items-end bg-slate-50 border border-slate-200 rounded-lg p-3">
          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">
              Type d'action
            </label>
            <select
              value={filterAction}
              onChange={(e) => setFilterAction(e.target.value)}
              className="h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm"
              data-testid="audit-filter-action"
            >
              <option value="">Toutes</option>
              {Object.entries(actionTypes).map(([key, meta]) => (
                <option key={key} value={key}>{meta.label || key}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">
              Gravité
            </label>
            <select
              value={filterSeverity}
              onChange={(e) => setFilterSeverity(e.target.value)}
              className="h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm"
              data-testid="audit-filter-severity"
            >
              <option value="">Toutes</option>
              <option value="info">Info</option>
              <option value="warn">Attention</option>
              <option value="crit">Critique</option>
            </select>
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">
              Acteur
            </label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input
                value={filterActor}
                onChange={(e) => setFilterActor(e.target.value)}
                placeholder="email admin"
                className="pl-10"
                data-testid="audit-filter-actor"
              />
            </div>
          </div>
        </div>

        {/* Liste */}
        {logs.length === 0 ? (
          <p className="text-sm text-slate-500 italic text-center py-10">
            Aucune action d'audit trouvée. Les actions critiques (approbations, blocages, nettoyage de doublons, etc.) s'afficheront ici dès qu'elles seront effectuées.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 border-b-2 border-slate-200">
                <tr>
                  <th className="text-left px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-600">Date</th>
                  <th className="text-left px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-600">Action</th>
                  <th className="text-left px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-600">Gravité</th>
                  <th className="text-left px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-600">Cible</th>
                  <th className="text-left px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-600">Impact</th>
                  <th className="text-left px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-600">Acteur</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {logs.map((log) => {
                  const Icon = ICONS[log.icon] || Info;
                  const sevClass = SEVERITY_STYLES[log.severity] || SEVERITY_STYLES.info;
                  return (
                    <tr key={log.id} className="hover:bg-slate-50" data-testid={`audit-row-${log.id}`}>
                      <td className="px-3 py-3 whitespace-nowrap text-xs text-slate-600 font-mono">
                        {formatDate(log.timestamp)}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center">
                            <Icon size={14} className="text-slate-700" />
                          </div>
                          <span className="font-black text-slate-800 text-xs">{log.action_label || log.event_type}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider border ${sevClass}`}>
                          {SEVERITY_LABEL[log.severity] || log.severity}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-xs">
                        {log.target?.name ? (
                          <div>
                            <div className="font-black text-slate-800">{log.target.name}</div>
                            <div className="text-slate-500 text-[10px] font-mono">
                              {log.target.type}:{(log.target.id || "").substring(0, 10)}
                              {log.target.email && ` · ${log.target.email}`}
                            </div>
                          </div>
                        ) : (
                          <span className="text-slate-400 italic">—</span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <Impact impact={log.impact} />
                      </td>
                      <td className="px-3 py-3 text-xs text-slate-600 font-mono truncate max-w-[180px]">
                        {log.actor || "system"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
